import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { fetchArticles, getPlaceholderImage } from '../utils/articleApi';

const Blog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const result = await fetchArticles({
          page: 1,
          limit: 4
        });
        setArticles(result.articles);
      } catch (error) {
        console.error('Error loading articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const SkeletonCard = () => (
    <div style={styles.card}>
      <div style={{ ...styles.imageWrapper, background: '#E0E4E8' }}></div>
      <div style={styles.cardContent}>
        <div style={{ ...styles.meta, marginBottom: '12px' }}>
          <div style={{ width: '100px', height: '14px', background: '#E0E4E8', borderRadius: '4px' }}></div>
          <div style={{ width: '60px', height: '14px', background: '#E0E4E8', borderRadius: '4px' }}></div>
        </div>
        <div style={{ width: '100%', height: '20px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '12px' }}></div>
        <div style={{ width: '100%', height: '14px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '8px' }}></div>
        <div style={{ width: '80%', height: '14px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '16px' }}></div>
        <div style={{ width: '100px', height: '14px', background: '#E0E4E8', borderRadius: '4px' }}></div>
      </div>
    </div>
  );

  return (
    <section id="blog" style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Health & Wellness Blog</h2>
          <p style={styles.subtitle}>
            Stay informed with the latest health tips, medical insights, and wellness advice
          </p>
        </div>

        {loading ? (
          <div style={styles.grid}>
            {[...Array(4)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <div style={styles.grid}>
            {articles.map((post) => (
              <article
                key={post.id}
                style={styles.card}
                className="hover-lift"
                onClick={() => navigate(`/article/${post.slug}`)}
                onMouseEnter={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(40, 108, 255, 0.12)';
                }}
                onMouseLeave={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={styles.imageWrapper}>
                  <img
                    src={post.image || getPlaceholderImage(400, 220)}
                    alt={post.title}
                    style={styles.image}
                    onError={(e) => {
                      e.target.src = getPlaceholderImage(400, 220);
                    }}
                  />
                  <div style={styles.badge}>{post.category}</div>
                </div>
                <div style={styles.cardContent}>
                  <div style={styles.meta}>
                    <span style={styles.date}>
                      {new Date(post.date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                    <span style={styles.readTime}>
                      <Clock size={14} style={{ marginRight: '4px' }} />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 style={styles.cardTitle}>{post.title}</h3>
                  <p style={styles.excerpt}>{post.excerpt}</p>
                  <button style={styles.readMore}>
                    Read More <ArrowRight size={16} style={{ marginLeft: '4px' }} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <div style={styles.viewAll}>
          <button 
            style={styles.btnSecondary} 
            onClick={() => navigate('/articles')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#286CFF';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#286CFF';
            }}
          >
            View All Articles
          </button>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '80px 0',
    background: '#F4F7FB'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  header: {
    textAlign: 'center',
    marginBottom: '64px'
  },
  title: {
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '1.2',
    color: '#4A4A4A',
    marginBottom: '16px'
  },
  subtitle: {
    fontSize: '18px',
    color: '#7C8A9A',
    maxWidth: '700px',
    margin: '0 auto'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '32px',
    marginBottom: '48px'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    border: '1px solid #E8ECF0'
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '220px',
    overflow: 'hidden'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease'
  },
  badge: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    background: '#FFD28A',
    color: '#4A4A4A',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700'
  },
  cardContent: {
    padding: '24px'
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#7C8A9A'
  },
  date: {
    fontSize: '14px'
  },
  readTime: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#4A4A4A',
    marginBottom: '12px',
    lineHeight: '1.3'
  },
  excerpt: {
    fontSize: '14px',
    color: '#7C8A9A',
    lineHeight: '1.5',
    marginBottom: '16px'
  },
  readMore: {
    background: 'none',
    border: 'none',
    color: '#286CFF',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '0',
    transition: 'color 0.2s ease'
  },
  viewAll: {
    display: 'flex',
    justifyContent: 'center'
  },
  btnSecondary: {
    background: 'transparent',
    color: '#286CFF',
    border: '2px solid #286CFF',
    borderRadius: '28px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '50px'
  }
};

export default Blog;
